import { useState, useEffect } from 'react';
import { Copy, Check, Terminal, Code, Zap, ExternalLink, Circle } from 'lucide-react';
import { useAuth } from '../lib/auth-context';

export function Quickstart() {
  const { tenant, api } = useAuth();
  const [copied, setCopied] = useState<string | null>(null);
  const [agentStatus, setAgentStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [apiKey, setApiKey] = useState<string>('');

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  // Check agent connection status
  useEffect(() => {
    if (!api || !tenant) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${api.getBaseUrl()}/api/v1/agent/status/${tenant.id}`);
        if (response.ok) {
          const data = await response.json();
          setAgentStatus(data.connected ? 'connected' : 'disconnected');
        } else {
          setAgentStatus('disconnected');
        }
      } catch {
        setAgentStatus('disconnected');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [api, tenant]);

  // Read API key from the current tab session only.
  useEffect(() => {
    const stored = sessionStorage.getItem('shadowguard_session');
    if (stored) {
      try {
        const config = JSON.parse(stored);
        setApiKey(config.apiKey || '');
      } catch {
        // Ignore
      }
    }
  }, []);

  const installCommand = 'npm install @shadowguard/express';
  const codeSnippet = `const express = require('express');
const { shadowguard } = require('@shadowguard/express');

const app = express();

// Add ShadowGuard middleware
app.use(shadowguard({
  apiKey: process.env.SHADOWGUARD_API_KEY
}));

// Your routes...
app.get('/', (req, res) => {
  res.json({ message: 'Protected by ShadowGuard!' });
});

app.listen(3000);`;

  const envContent = apiKey ? `SHADOWGUARD_API_KEY=${apiKey}` : 'SHADOWGUARD_API_KEY=your_api_key_here';

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Get Started with ShadowGuard SDK</h2>
        <p className="text-gray-400">Add enterprise-grade threat detection to your Express.js app in 3 lines of code</p>
      </div>

      {/* Agent Connection Status */}
      <div className={`mb-8 p-4 rounded-xl border ${
        agentStatus === 'connected' 
          ? 'bg-green-500/5 border-green-500/20' 
          : agentStatus === 'disconnected'
          ? 'bg-red-500/5 border-red-500/20'
          : 'bg-gray-500/5 border-gray-500/20'
      }`}>
        <div className="flex items-center gap-3">
          <Circle className={`w-3 h-3 ${
            agentStatus === 'connected' 
              ? 'text-green-400 fill-green-400' 
              : agentStatus === 'disconnected'
              ? 'text-red-400'
              : 'text-gray-400'
          }`} />
          <span className="font-semibold text-white">
            {agentStatus === 'connected' ? 'Agent Connected' : agentStatus === 'disconnected' ? 'No Agent Connected' : 'Checking Status...'}
          </span>
          {agentStatus === 'disconnected' && (
            <span className="text-sm text-gray-400">Follow the steps below to connect your first agent</span>
          )}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-6">
        {/* Step 1: Install */}
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Terminal className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Step 1: Install the SDK</h3>
              <p className="text-sm text-gray-400">Add the ShadowGuard SDK to your Express.js project</p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs">TERMINAL</span>
              <button
                onClick={() => copyToClipboard(installCommand, 'install')}
                className="p-1.5 hover:bg-white/5 rounded transition-colors"
              >
                {copied === 'install' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
            <code className="text-green-400">{installCommand}</code>
          </div>

          <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-300">
              <strong>Note:</strong> The SDK is currently in beta. Until npm publishing is complete, use the local path from the GitHub repo.
            </p>
          </div>
        </div>

        {/* Step 2: Configure */}
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Code className="w-5 h-5 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Step 2: Add Middleware</h3>
              <p className="text-sm text-gray-400">Integrate ShadowGuard into your Express app (just 3 lines!)</p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs">app.js</span>
              <button
                onClick={() => copyToClipboard(codeSnippet, 'code')}
                className="p-1.5 hover:bg-white/5 rounded transition-colors"
              >
                {copied === 'code' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
            <pre className="text-gray-300 overflow-x-auto">
              <code>{codeSnippet}</code>
            </pre>
          </div>
        </div>

        {/* Step 3: Environment Variable */}
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Step 3: Add Your API Key</h3>
              <p className="text-sm text-gray-400">Create a .env file with your ShadowGuard credentials</p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 text-xs">.env</span>
              <button
                onClick={() => copyToClipboard(envContent, 'env')}
                className="p-1.5 hover:bg-white/5 rounded transition-colors"
              >
                {copied === 'env' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
            <code className="text-amber-400">{envContent}</code>
          </div>

          {!apiKey && (
            <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-300">
                <strong>Can't find your API key?</strong> It was provided when you logged in. Check your password manager or generate a new one using the SDK onboarding endpoint.
              </p>
            </div>
          )}
        </div>

        {/* Additional Resources */}
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-6 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-white mb-4">Additional Resources</h3>
          
          <div className="space-y-3">
            <a
              href="https://github.com/canboigay/shadowguard/blob/main/GET_STARTED.md"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.06] rounded-lg transition-colors group"
            >
              <div>
                <div className="text-white font-medium mb-1">Full Documentation</div>
                <div className="text-sm text-gray-400">Complete setup guide with examples</div>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
            </a>

            <a
              href="https://github.com/canboigay/shadowguard/blob/main/BETA_TESTING.md"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.06] rounded-lg transition-colors group"
            >
              <div>
                <div className="text-white font-medium mb-1">Beta Testing Guide</div>
                <div className="text-sm text-gray-400">Test scenarios and troubleshooting</div>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
            </a>

            <a
              href="https://github.com/canboigay/shadowguard/tree/main/examples/express-basic"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-3 bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.06] rounded-lg transition-colors group"
            >
              <div>
                <div className="text-white font-medium mb-1">Example Application</div>
                <div className="text-sm text-gray-400">Working demo with attack patterns</div>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
            </a>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3">What Happens Next?</h3>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span>ShadowGuard collects HTTP telemetry from all requests (method, path, headers, timing)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span>Attack patterns are detected automatically (SQLi, XSS, path traversal, command injection)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span>Sessions are fingerprinted using IP + User-Agent + headers</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span>All telemetry is encrypted (AES-256-GCM) before transmission</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              <span>Your app performance is unaffected (non-blocking, graceful error handling)</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
